import { useEffect, useState } from 'react';
import { useExperienceField } from '../../hooks/useTripDataFields';

interface TransitionsParams {
  dayIndex: number;
  experience: any;
  experienceExtras: any[];
  experienceStartIndex: number;
  days: any[];
  updateDay: (dayIndex: number, patch: Record<string, unknown>) => void;
  showExtrasModal: boolean;
  setShowExtrasModal: (open: boolean) => void;
}

// Extras of the current experience that recommend the next one.
const getTransitionOptions = (currentExp: any, nextExp: any) => {
  const options: any[] = [];

  currentExp?.extras?.forEach((e: any) => {
    if (e?.recommendations?.find((r: any) => r.id === nextExp?.id)) {
      options.push({
        ...e,
        sourceExperience: currentExp,
        sourceExperienceId: currentExp?.id,
        transitionalExperienceId: nextExp?.id,
      });
    }
  });

  return options.filter(Boolean);
};

// Suggests the transition extras a following experience recommends, and
// clears the last day's `skipTransitionOption` once that following
// experience is removed.
export function useTripDayExperienceTransitions({
  dayIndex,
  experience,
  experienceExtras,
  experienceStartIndex,
  days,
  updateDay,
  showExtrasModal,
  setShowExtrasModal,
}: TransitionsParams) {
  const [{ value: nextExperience }] = useExperienceField(dayIndex + 1);
  const [extraRecommendationMode, setExtraRecommendationMode] = useState(false);
  const [recommendedExtras, setRecommendedExtras] = useState<any[]>([]);

  useEffect(() => {
    if (
      experience?.id &&
      experience?.extras &&
      nextExperience?.extras &&
      nextExperience?.id
    ) {
      const options = getTransitionOptions(experience, nextExperience);

      const hasSelectedOption = experienceExtras.some((extra) =>
        options?.some(
          (option) =>
            option.id === extra.id && extra.experienceId === experience?.id,
        ),
      );

      // Calculate the last day of the experience (where the transition happens)
      const lastDayIndex =
        experienceStartIndex + (experience?.durationDays ?? 1) - 1;

      if (
        options.length > 0 &&
        !hasSelectedOption &&
        !days[lastDayIndex]?.skipTransitionOption
      ) {
        setRecommendedExtras(options);
        setExtraRecommendationMode(true);
        setShowExtrasModal(true);
      }
    }
    // Recommends on product changes only: both experiences are read whole,
    // and `setShowExtrasModal` comes from the caller, not a stable setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    experience?.id,
    experience?.extras,
    experience?.durationDays,
    experienceStartIndex,
    days,
    nextExperience?.id,
    nextExperience?.extras,
    experienceExtras,
  ]);

  useEffect(() => {
    if (!showExtrasModal && extraRecommendationMode) {
      setExtraRecommendationMode(false);
    }
    // Leaves recommendation mode when the modal closes, never on the mode
    // turning on (it turns on while the modal opens).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showExtrasModal]);

  useEffect(() => {
    // When the next experience is removed, clear the skipTransitionOption on the last day
    const lastDayIndex =
      experienceStartIndex + (experience?.durationDays ?? 1) - 1;
    if (days[lastDayIndex]?.skipTransitionOption && !nextExperience?.id) {
      updateDay(lastDayIndex, { skipTransitionOption: false });
    }
  }, [
    nextExperience?.id,
    experience?.id,
    experience?.durationDays,
    experienceStartIndex,
    days,
    updateDay,
  ]);

  return { nextExperience, recommendedExtras, extraRecommendationMode };
}
